
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useStore } from '../../store';
import { PRIORITY, TASK_TYPE, STATUS, fmt } from '../../utils';
import { ZoomIn, ZoomOut, Calendar } from 'lucide-react';

const SCREEN = window.innerWidth;

let DAY_W_DEFAULT;
let ROW_H;
let LABEL_W;

// Desktop 1920+
if (SCREEN >= 1600) {
  DAY_W_DEFAULT = 60;
  ROW_H = 50;
  LABEL_W = 220;
}

// Laptop
else if (SCREEN >= 1200) {
  DAY_W_DEFAULT = 50;
  ROW_H = 44;
  LABEL_W = 200;
}

// Tablet
else if (SCREEN >= 768) {
  DAY_W_DEFAULT = 40;
  ROW_H = 38;
  LABEL_W = 160;
}

// Phone
else {
  DAY_W_DEFAULT = 30;
  ROW_H = 32;
  LABEL_W = 120;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function diffDays(a, b) {
  return Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
}

function isSameDay(a, b) {
  return a.toDateString() === b.toDateString();
}

function isWeekend(d) {
  return d.getDay() === 0 || d.getDay() === 6;
}

function getRange(tasks) {
  const hasDates = tasks.filter(t => t.createdAt || t.dueDate);

  if (hasDates.length === 0) {
    const now = startOfDay(new Date());
    return { start: addDays(now, -30), days: 120 };
  }

  const dates = hasDates
    .flatMap(t => [
      t.createdAt && new Date(t.createdAt),
      t.dueDate && new Date(t.dueDate)
    ])
    .filter(Boolean);

  const minD = startOfDay(new Date(Math.min(...dates)));
  const maxD = startOfDay(new Date(Math.max(...dates)));

  const span = diffDays(minD, maxD) + 1;

  return {
    start: addDays(minD, -180),
    days: span + 360
  };
}

function MonthHeader({ start, days, dayW }) {
  const months = [];
  let cur = new Date(start);

  while (diffDays(start, cur) < days) {
    const month = cur.getMonth();
    const year = cur.getFullYear();

    let count = 0;
    const ms = new Date(cur);

    while (diffDays(start, cur) < days && cur.getMonth() === month) {
      count++;
      cur = addDays(cur, 1);
    }

    months.push({
      label: ms.toLocaleDateString('ru', { month: 'long', year: 'numeric' }),
      days: count
    });
  }

  return (
    <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
      {months.map((m, i) => (
        <div
          key={i}
          className="text-[14px] font-semibold text-gray-500 dark:text-gray-400 px-2 py-1.5 border-r border-gray-200 dark:border-gray-700 truncate capitalize"
          style={{ width: m.days * dayW }}
        >
          {m.label}
        </div>
      ))}
    </div>
  );
}

function DayHeader({ start, days, dayW }) {
  return (
    <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
      {Array.from({ length: days }, (_, i) => {
        const d = addDays(start, i);
        const today = isSameDay(d, new Date());
        const wknd = isWeekend(d);

        return (
          <div
            key={i}
            style={{ width: dayW, minWidth: dayW }}
            className={`text-center text-[12px] py-1 border-r border-gray-100 dark:border-gray-800 flex-shrink-0
            ${today ? 'font-bold text-brand-600 dark:text-brand-400'
                : wknd
                  ? 'text-gray-300 dark:text-gray-600'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
          >
            {dayW >= 20 ? d.getDate() : (d.getDate() % 5 === 0 ? d.getDate() : '')}
          </div>
        );
      })}
    </div>
  );
}

function TaskBar({ task, rowIdx, start, dayW, onClick }) {
  const hasEnd = task.dueDate;
  const hasStart = task.createdAt;

  // Only show tasks that have a due date
  if (!hasEnd) return null;

  const barStart = hasStart
    ? diffDays(start, new Date(task.createdAt))
    : diffDays(start, new Date(task.dueDate));

  const barEnd = hasEnd
    ? diffDays(start, new Date(task.dueDate))
    : barStart;

  const barDays = Math.max(barEnd - barStart + 1, 1);

  const s = STATUS[task.status] || STATUS.backlog;
  const isOverdue = hasEnd && new Date(task.dueDate) < new Date() && task.status !== 'done' && task.status !== 'review';
  const isDone = task.status === 'done';

  const barColor = isDone ? '#34d399' : isOverdue ? '#ef4444' : (s.color || '#4f63ff');

  return (
    <div
      onClick={onClick}
      className="absolute flex items-center rounded-md cursor-pointer hover:brightness-90 transition-all"
      style={{
        left: barStart * dayW,
        width: Math.max(barDays * dayW - 2, dayW - 2),
        top: rowIdx * ROW_H + 6,
        height: ROW_H - 12,
        background: barColor
      }}
      title={`${task.title}`}
    >
      {barDays * dayW > 60 && (
        <span className="px-2 text-white text-[11px] font-medium truncate">
          {task.title}
        </span>
      )}
    </div>
  );
}

export default function GanttView({ compact = false }) {

  const { tasks, openTask } = useStore();

  const containerRef = useRef();
  const chartScrollRef = useRef();

  const [dayW, setDayW] = useState(DAY_W_DEFAULT);

  useEffect(() => {
    if (!containerRef.current) return;

    const w = containerRef.current.offsetWidth - LABEL_W;

    setDayW(compact ? Math.max(14, Math.min(22, w / 45)) : DAY_W_DEFAULT);

  }, [compact]);

  const allTasks = useMemo(() => tasks.filter(t => !t.parent), [tasks]);

  const { start, days } = useMemo(() => {
    return getRange(allTasks);
  }, [allTasks]);

  const today = startOfDay(new Date());
  const todayOffset = diffDays(start, today);

  useEffect(() => {
    if (!chartScrollRef.current) return;

    chartScrollRef.current.scrollLeft = todayOffset * dayW;

  }, [todayOffset, dayW]);

  const zoomIn = () => setDayW(w => Math.min(w + 6, 80));
  const zoomOut = () => setDayW(w => Math.max(w - 6, 12));

  const totalW = days * dayW;

  return (
    <div className="flex flex-col h-full">

      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">

        <button
          onClick={() => {
            if (!chartScrollRef.current) return;
            chartScrollRef.current.scrollLeft = todayOffset * dayW;
          }}
          className="btn-secondary py-1 px-2.5 text-xs"
        >
          <Calendar className="w-3.5 h-3.5" /> Сегодня
        </button>

        <div className="flex items-center gap-1 ml-auto">

          <button onClick={zoomOut} className="btn-ghost p-1.5">
            <ZoomOut className="w-4 h-4" />
          </button>

          <button onClick={zoomIn} className="btn-ghost p-1.5">
            <ZoomIn className="w-4 h-4" />
          </button>

        </div>

      </div>

      {allTasks.length === 0 ? (

        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">

          <div className="text-center">

            <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />

            <p>Нет задач с датами</p>
            <p className="text-xs mt-1">Установите дату начала или срок у задач</p>

          </div>

        </div>

      ) : (

        <div ref={containerRef} className="flex-1 overflow-hidden flex">

          <div
            className="flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto"
            style={{ width: LABEL_W }}
          >

            <div
              style={{ height: 61 }}
              className="border-b border-gray-200 dark:border-gray-800 flex items-end pb-1 px-3"
            >
              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                Задача
              </span>
            </div>

            {allTasks.map((task, i) => {

              const p = PRIORITY[task.priority];

              return (
                <div
                  key={task._id}
                  onClick={() => openTask(task._id)}
                  className="flex items-center gap-2 px-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-50 dark:border-gray-800/50"
                  style={{ height: ROW_H }}
                >

                  <span className="text-xs flex-shrink-0">
                    {TASK_TYPE[task.type]?.icon}
                  </span>

                  <span className="text-xs font-medium truncate flex-1">
                    {task.title}
                  </span>

                  <span className="text-[10px] flex-shrink-0">
                    {p?.icon}
                  </span>

                </div>
              );

            })}

          </div>

          <div ref={chartScrollRef} className="flex-1 overflow-auto">

            <div style={{ width: totalW, position: 'relative' }}>

              <MonthHeader start={start} days={days} dayW={dayW} />
              <DayHeader start={start} days={days} dayW={dayW} />

              <div style={{ position: 'relative', height: allTasks.length * ROW_H }}>

                {Array.from({ length: days }, (_, i) => {

                  const d = addDays(start, i);
                  const wknd = isWeekend(d);
                  const todays = isSameDay(d, today);

                  return (
                    <div
                      key={i}
                      className={`absolute top-0 bottom-0 border-r border-gray-100 dark:border-gray-800/60
                      ${wknd ? 'bg-gray-50/70 dark:bg-gray-800/30' : ''}
                      ${todays ? 'bg-brand-50/40 dark:bg-brand-900/10' : ''}
                      `}
                      style={{ left: i * dayW, width: dayW }}
                    />
                  );

                })}

                {todayOffset >= 0 && todayOffset < days && (

                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-brand-500 z-10"
                    style={{ left: todayOffset * dayW + dayW / 2 }}
                  />

                )}

                {allTasks.map((task, i) => (
                  <TaskBar
                    key={task._id}
                    task={task}
                    rowIdx={i}
                    start={start}
                    dayW={dayW}
                    onClick={() => openTask(task._id)}
                  />
                ))}

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}

